import {useAsyncAction} from "nate-react-api-helpers";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {useSnackbar} from "../../../../misc/Snackbar";
import {useCallback, useState} from "react";

// throw from a snack action to stop it without showing an error (e.g. the user cancelled a confirm)
export class SnackActionCancelled extends Error {}

// return SnackCancel from a callback to end the action quietly — the loading snack is
// hidden and no success message shows (e.g. the user backed out of a confirm dialog
// mid-action).
export const SnackCancel = Symbol("snack-cancel");

export function useSnackAction<T, U>(
    callback: (input: any) => Promise<U>,
    props: {
        // no loading or success snack when omitted
        success?: string;
    }) {

    const callbackRef = useSyncedRef(callback);
    const successRef = useSyncedRef(props.success);
    const snack = useSnackbar();
    const [loading, setLoading] = useState(false)

    const action = useAsyncAction(async (input) => {
        try {
            setLoading(true)
            // without a success message the caller shows its own progress
            if (successRef.current) snack.loading();
            const result = await callbackRef.current(input);
            if (result === SnackCancel) {
                if (successRef.current) snack.hide();
            } else if (successRef.current) {
                snack.success(successRef.current)
            }
        } catch (e: any) {
            if (e instanceof SnackActionCancelled) {
                if (successRef.current) snack.hide();
            } else {
                console.error(e);
                snack.error(e.message);
            }
        }

        setLoading(false)
    }, []);

    const actionCallbackRef = useSyncedRef(action.callback)
    const cb = useCallback((input: T) => actionCallbackRef.current(input), [actionCallbackRef])

    return {
        callback: cb,
        loading,
    }
}